// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Filter, Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ChainSettingEntry } from "@/api/hooks/use-chains";
import { useChainSettingsQuery } from "@/api/hooks/use-chains";
import { SettingsCompareIcon } from "@/components/icons";
import { PageHeader } from "@/components/layout/page-header";
import { FilterToggle } from "@/components/shared/filter-toggle";
import { KeyChip } from "@/components/shared/key-chip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { SettingValueCell } from "@/features/settings/setting-value-cell";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";

interface ChainSettingsCompareProps {
  chainNameA: string;
  chainNameB: string;
}

interface CompareRow {
  settingId: number;
  key: string;
  name: string;
  fieldType: ChainSettingEntry["fieldType"];
  valueA?: string;
  sourceA?: "override" | "default";
  valueB?: string;
  sourceB?: "override" | "default";
  isDiff: boolean;
}

function effectiveValue(entry: ChainSettingEntry): string {
  return entry.overrideValue ?? entry.defaultValue;
}

function effectiveSource(entry: ChainSettingEntry): "override" | "default" {
  return entry.overrideId != null ? "override" : "default";
}

function SourceBadge({ source }: { source: "override" | "default" }) {
  return (
    <Badge
      variant="secondary"
      className={
        source === "override"
          ? "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300"
          : "text-muted-foreground"
      }
    >
      {source}
    </Badge>
  );
}

function CompareValueCell({
  value,
  source,
  fieldType,
  name,
}: {
  value?: string;
  source?: "override" | "default";
  fieldType: string;
  name: string;
}) {
  if (value === undefined) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <div className="flex flex-col items-start gap-1">
      <SettingValueCell value={value} fieldType={fieldType} name={name} />
      {source && <SourceBadge source={source} />}
    </div>
  );
}

export function ChainSettingsCompare({ chainNameA, chainNameB }: ChainSettingsCompareProps) {
  const { t } = useTranslation("back-office");
  const [onlyDiffs, setOnlyDiffs] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const flushSearch = useCallback((value: string) => {
    clearTimeout(debounceRef.current);
    setDebouncedSearch(value);
  }, []);

  function handleSearchInput(value: string) {
    setSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), 300);
  }

  useEffect(function cleanupSearchDebounceTimer() {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const { data: ctx } = useBackofficeContext();

  const { data: settingsAData, isSuccess: settingsASuccess } = useChainSettingsQuery({
    chainName: chainNameA,
    limit: -1,
  });

  const { data: settingsBData, isSuccess: settingsBSuccess } = useChainSettingsQuery({
    chainName: chainNameB,
    limit: -1,
  });

  const settingsReady = settingsASuccess && settingsBSuccess;

  const displayNameA = ctx?.chains?.find((c) => c.value === chainNameA)?.display ?? chainNameA;
  const displayNameB = ctx?.chains?.find((c) => c.value === chainNameB)?.display ?? chainNameB;

  const breadcrumbs = useMemo(() => [{ label: t("chains.title"), to: "/chains" }], [t]);

  const rows = useMemo<CompareRow[]>(() => {
    const mapA = new Map<number, ChainSettingEntry>((settingsAData?.settings ?? []).map((s) => [s.settingId, s]));
    const mapB = new Map<number, ChainSettingEntry>((settingsBData?.settings ?? []).map((s) => [s.settingId, s]));

    const allIds = new Set([...mapA.keys(), ...mapB.keys()]);
    const result: CompareRow[] = [];

    for (const id of allIds) {
      const a = mapA.get(id);
      const b = mapB.get(id);
      const entry = (a ?? b)!;

      result.push({
        settingId: id,
        key: entry.key,
        name: entry.name,
        fieldType: entry.fieldType,
        valueA: a ? effectiveValue(a) : undefined,
        sourceA: a ? effectiveSource(a) : undefined,
        valueB: b ? effectiveValue(b) : undefined,
        sourceB: b ? effectiveSource(b) : undefined,
        isDiff: effectiveValue(a ?? entry) !== effectiveValue(b ?? entry) || !a || !b,
      });
    }

    result.sort((a, b) => a.name.localeCompare(b.name));
    return result;
  }, [settingsAData, settingsBData]);

  const visibleRows = useMemo(() => {
    let filtered = rows;
    if (onlyDiffs) filtered = filtered.filter((r) => r.isDiff);
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      filtered = filtered.filter((r) => r.name.toLowerCase().includes(q) || r.key.toLowerCase().includes(q));
    }
    return filtered;
  }, [rows, onlyDiffs, debouncedSearch]);

  const isFiltered = !onlyDiffs || debouncedSearch.length > 0;

  return (
    <div>
      <PageHeader
        title={t("chains.compare.title")}
        icon={SettingsCompareIcon}
        subtitle={
          <>
            <KeyChip label={chainNameA} variant="chain" />
            <span className="text-muted-foreground text-xs">vs</span>
            <KeyChip label={chainNameB} variant="chain" />
          </>
        }
        breadcrumbs={breadcrumbs}
      />

      <div className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <form
            className="relative"
            onSubmit={(e) => {
              e.preventDefault();
              flushSearch(search);
            }}
          >
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => handleSearchInput(e.target.value)}
              placeholder={t("settings.list.searchPlaceholder")}
              className="pl-9 w-[250px]"
            />
          </form>
          <FilterToggle
            active={onlyDiffs}
            onChange={setOnlyDiffs}
            icon={Filter}
            label={t("chains.compare.onlyDiffs")}
          />
          {isFiltered && (
            <Button
              variant="ghost"
              className="h-9 px-2"
              onClick={() => {
                setOnlyDiffs(true);
                setSearch("");
                flushSearch("");
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        <div className="rounded-md border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-sidebar">
                <th className="text-left h-10 px-3 font-medium text-sm w-[260px] rounded-tl-md">
                  {t("settings.columns.setting")}
                </th>
                <th className="text-left h-10 px-3 font-medium text-sm w-[220px] border-l">{displayNameA}</th>
                <th className="text-left h-10 px-3 font-medium text-sm w-[220px] border-l rounded-tr-md">
                  {displayNameB}
                </th>
              </tr>
            </thead>
            <tbody>
              {!settingsReady &&
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={`skeleton-${i}`} className="border-b last:border-b-0">
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-1">
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="h-4 w-24" />
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <Skeleton className="h-5 w-32" />
                    </td>
                    <td className="px-3 py-2">
                      <Skeleton className="h-5 w-32" />
                    </td>
                  </tr>
                ))}
              {settingsReady && visibleRows.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-center py-8 text-muted-foreground">
                    {onlyDiffs && !debouncedSearch ? t("chains.compare.noDifferences") : t("common.noResults")}
                  </td>
                </tr>
              )}
              {settingsReady &&
                visibleRows.map((row) => (
                  <tr
                    key={row.settingId}
                    className={
                      !onlyDiffs && row.isDiff
                        ? "bg-amber-50 dark:bg-amber-950/20 border-b last:border-b-0"
                        : "border-b last:border-b-0 hover:bg-muted/30"
                    }
                  >
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-1">
                        <span>{row.name}</span>
                        <KeyChip label={row.key} />
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <CompareValueCell
                        value={row.valueA}
                        source={row.sourceA}
                        fieldType={row.fieldType}
                        name={row.name}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <CompareValueCell
                        value={row.valueB}
                        source={row.sourceB}
                        fieldType={row.fieldType}
                        name={row.name}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
